// storyFrame — the bridge between the story source and the live page in the
// editor's iframe (#1416).
//
// The iframe loads the web app itself (same origin), so the page renders with
// its own skin, runtime and datasets. The editor then takes over the story:
// it swaps the mounted <main class="pt-story"> for one built from the draft
// source, stamps every element with its ordinal BEFORE hydration (so a click
// on anything maps back to a byte range in the source), hydrates it with the
// page's own `hydrate`, and marks what can be typed into and what is locked.
//
// Nothing here serialises the hydrated page. A text edit re-writes ONLY the
// edited element's content: its text is read from the DOM, its inline figures
// and inline markup are copied back from their own source bytes.

import {
  INLINE_TAGS,
  blockOf,
  editInner,
  endTagSource,
  escapeText,
  isEditableProse,
  isHidden,
  isLeafTag,
  outerSource,
  parseStory,
  setAttr,
  settingsHostOf,
  startTagSource,
  STORY_VOCABULARY,
  type StoryDoc,
} from './storySource.js'

/** The ordinal stamp: `nodes[n]` in the parse is the element carrying `data-pt-src-idx="n"`. */
export const IDX_ATTR = 'data-pt-src-idx'
/** What an element is to the editor: `prose` (type into it), `attr` (type into it; writes an attribute), `leaf` (locked), `block` (a top-level section). */
export const ROLE_ATTR = 'data-pt-ed'
/**
 * On an element the page's runtime drew for one of a box's named inputs (a
 * heading, a caption, a button's words): the input's name. story.js draws
 * every text input it shows this way (B3), so the editor needs no code per box.
 */
export const INPUT_ATTR = 'data-pt-input'
/** On an `attr` element: the ordinal of the box the attribute belongs to, and its name. */
export const HOST_ATTR = 'data-pt-ed-host'
export const FIELD_ATTR = 'data-pt-ed-attr'
export const HIDDEN_ATTR = 'data-pt-ed-hidden'
/** On a top-level block: its bytes differ from the last saved draft (#1578). Editor chrome only, never serialised. */
export const DIRTY_ATTR = 'data-pt-ed-dirty'
export const EDITOR_STYLE_ID = 'pt-story-editor-style'
/**
 * The section spotlight: on `main`, the ordinal of the section a section
 * control will act on; on that section, the label drawn above its outline.
 * Editor chrome only. The source is edited by byte ranges and never
 * serialised from the page, so neither attribute can reach story.html.
 */
export const SPOT_ATTR = 'data-pt-ed-spot'
export const SPOT_LABEL_ATTR = 'data-pt-ed-spot-label'

/** Formatting a user can add with the browser's own shortcuts, kept when their edit is written back. */
const TYPED_INLINE = new Set(['b', 'strong', 'i', 'em', 'u'])

export interface StoryRuntime {
  /** story.js `hydrate`. `includeHidden` fills hidden sections too (an older lib/story.js ignores it and fills everything). */
  hydrate: (root: Element, opts?: { includeHidden?: boolean }) => Promise<string[]>
  /** story.js `STORY_TAGS`: the boxes this runtime knows. */
  STORY_TAGS?: readonly string[]
}

export interface MountResult {
  main: HTMLElement
  /** Box errors from hydration (each also drew a red panel in place). */
  errors: string[]
  /** False when the browser built a different tree from the one parsed here: editing is unsafe. */
  parity: boolean
  /**
   * The page's runtime draws the story kit's named inputs (B3; it knows
   * pt-callout). An app whose lib/story.js is older ignores them, so the
   * editor offers no box settings there: an Apply would change nothing seen.
   */
  kit: boolean
}

/** Editor chrome inside the page. Outlines, never layout: the page must look like itself. */
export const EDITOR_CSS = `
main.pt-story[data-pt-editor] [${ROLE_ATTR}="prose"],
main.pt-story[data-pt-editor] [${ROLE_ATTR}="attr"] {
  outline: 1px dashed transparent; outline-offset: 3px; border-radius: 2px;
  transition: outline-color 120ms ease; cursor: text;
}
main.pt-story[data-pt-editor] [${ROLE_ATTR}="prose"]:hover,
main.pt-story[data-pt-editor] [${ROLE_ATTR}="attr"]:hover { outline-color: rgba(2,136,209,0.45); }
main.pt-story[data-pt-editor] [${ROLE_ATTR}="prose"]:focus,
main.pt-story[data-pt-editor] [${ROLE_ATTR}="attr"]:focus { outline: 2px solid rgba(2,136,209,0.85); }
main.pt-story[data-pt-editor] [${ROLE_ATTR}="leaf"] { cursor: default; border-radius: 4px; transition: box-shadow 120ms ease; }
main.pt-story[data-pt-editor] [${ROLE_ATTR}="leaf"]:hover { box-shadow: 0 0 0 1px rgba(38,55,87,0.22); }
main.pt-story[data-pt-editor] pt-num[${ROLE_ATTR}="leaf"] { display: inline; }
main.pt-story[data-pt-editor] > [${ROLE_ATTR}="block"] { position: relative; }
main.pt-story[data-pt-editor] > [hidden] { display: block !important; opacity: 0.42; }
main.pt-story[data-pt-editor] > [${HIDDEN_ATTR}]::before {
  content: 'Hidden'; position: absolute; top: -22px; left: 0;
  font: 600 11px/18px system-ui, sans-serif; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 0 8px; border-radius: 999px; background: rgba(38,55,87,0.08); color: rgba(38,55,87,0.8);
}
main.pt-story[data-pt-editor] > [${DIRTY_ATTR}] { outline: 2px solid rgba(237,108,2,0.85); outline-offset: 3px; border-radius: 4px; }
main.pt-story[data-pt-editor] > [${DIRTY_ATTR}]::before {
  content: 'Unsaved'; position: absolute; top: -22px; right: 0; z-index: 1;
  font: 600 11px/18px system-ui, sans-serif; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 0 8px; border-radius: 999px; background: rgb(237,108,2); color: #fff;
}
main.pt-story[data-pt-editor] a { cursor: text; }
main.pt-story[data-pt-editor] > * { transition: opacity 160ms ease-out; }
main.pt-story[data-pt-editor][${SPOT_ATTR}] > :not([${SPOT_LABEL_ATTR}]) { opacity: 0.38; }
main.pt-story[data-pt-editor] > [${SPOT_LABEL_ATTR}] {
  position: relative; opacity: 1;
  outline: 2px solid rgba(2,136,209,0.9); outline-offset: 10px; border-radius: 4px;
}
main.pt-story[data-pt-editor] > [${SPOT_LABEL_ATTR}]::before { display: none; }
main.pt-story[data-pt-editor] > [${SPOT_LABEL_ATTR}]::after {
  content: attr(${SPOT_LABEL_ATTR}); position: absolute; top: -34px; left: -10px; z-index: 2;
  font: 600 11px/20px system-ui, sans-serif; letter-spacing: 0.02em; white-space: nowrap;
  padding: 0 10px; border-radius: 999px; background: rgb(2,136,209); color: #fff;
  pointer-events: none;
}
@media (prefers-reduced-motion: reduce) {
  main.pt-story[data-pt-editor] > * { transition: none; }
}
@media (prefers-reduced-motion: no-preference) {
  main.pt-story[data-pt-editor] > * { animation: none !important; }
}
`

// ---------------------------------------------------------------------------
// Stamping and marking
// ---------------------------------------------------------------------------

/**
 * Stamp every element under `main` with its ordinal. Returns false (and
 * stamps nothing) when the browser's tree does not match the parse — then no
 * click could be trusted to map to the right bytes.
 */
export function stampElements(main: Element, doc: StoryDoc): boolean {
  const els = [...main.querySelectorAll('*')]
  if (els.length !== doc.nodes.length) return false
  for (let i = 0; i < els.length; i++) {
    if (els[i].localName.toLowerCase() !== doc.nodes[i].tag) return false
  }
  els.forEach((el, i) => el.setAttribute(IDX_ATTR, String(i)))
  return true
}

export function idxOf(el: Element | null | undefined): number {
  const v = el?.getAttribute(IDX_ATTR)
  return v == null ? -1 : Number(v)
}

function markAttr(el: Element | null | undefined, host: number, field: string) {
  if (!el) return
  el.setAttribute(ROLE_ATTR, 'attr')
  el.setAttribute(HOST_ATTR, String(host))
  el.setAttribute(FIELD_ATTR, field)
  el.setAttribute('contenteditable', 'true')
  el.setAttribute('spellcheck', 'true')
}

/**
 * Mark the hydrated page for editing: prose becomes editable, boxes that show
 * data become locked, the headings hydration drew from attributes become
 * editable fields that write those attributes, and hidden sections are
 * flagged so they draw dimmed with a "Hidden" tag rather than vanishing.
 */
export function markEditable(main: Element, doc: StoryDoc) {
  for (const el of main.querySelectorAll(`[${IDX_ATTR}]`)) {
    const idx = idxOf(el)
    const node = doc.nodes[idx]
    if (!node) continue
    if (isLeafTag(node.tag)) {
      el.setAttribute(ROLE_ATTR, 'leaf')
      el.setAttribute('contenteditable', 'false')
      if (node.tag === 'pt-kpi') {
        markAttr(el.querySelector(':scope > .kpi > .kpi-label'), idx, 'label')
      }
    } else if (isEditableProse(doc, idx)) {
      el.setAttribute(ROLE_ATTR, 'prose')
      el.setAttribute('contenteditable', 'true')
      el.setAttribute('spellcheck', 'true')
    } else if (node.parent === -1) {
      el.setAttribute(ROLE_ATTR, 'block')
    }
    if (node.parent === -1 && isHidden(doc, idx)) el.setAttribute(HIDDEN_ATTR, '')
    else el.removeAttribute(HIDDEN_ATTR)

    if (node.tag === 'pt-hero') {
      // An app whose lib/story.js predates #1378 (1b727456b) renders the
      // title and subtitle as direct children of pt-hero, with no
      // .pt-hero-text wrapper: match both shapes.
      markAttr(el.querySelector(':scope > .pt-hero-text > h1, :scope > h1'), idx, 'title')
      markAttr(el.querySelector(':scope > .pt-hero-text > .pt-subtitle, :scope > .pt-subtitle'), idx, 'subtitle')
      markAttr(el.querySelector(':scope > .pt-hero-figure > .pt-verdict'), idx, 'verdict')
    } else if (node.tag === 'pt-section' && node.attrs.title !== undefined) {
      markAttr(el.querySelector(':scope > h2:not([' + IDX_ATTR + '])'), idx, 'title')
    }
  }
  // The named inputs a runtime from B3 on draws: each is a field of the
  // nearest stamped box around it, when that box has a text input by that
  // name (a leaf's data inputs are never typeable). The two passes above
  // stay for apps whose lib/story.js predates data-pt-input.
  for (const el of main.querySelectorAll(`[${INPUT_ATTR}]`)) {
    const host = el.parentElement?.closest(`[${IDX_ATTR}]`)
    const idx = idxOf(host)
    const name = el.getAttribute(INPUT_ATTR) ?? ''
    const node = doc.nodes[idx]
    if (node && STORY_VOCABULARY[node.tag]?.inputs[name] === 'text') markAttr(el, idx, name)
  }
}

// ---------------------------------------------------------------------------
// Writing an edit back
// ---------------------------------------------------------------------------

interface Emitted {
  el: Element
  /** How many source elements this DOM element stands for: 1, or a copied subtree's size. */
  size: number
}

/**
 * The source for an edited element's content. Text comes from the DOM;
 * elements that were in the source (a figure, a link, emphasis) are written
 * back from their own bytes, so a figure's attributes and a link's href are
 * never re-serialised. Formatting the browser added (bold, italic) is kept as
 * bare tags; anything else it added is unwrapped to its text.
 */
export function serializeInner(el: Element, doc: StoryDoc): { html: string; emitted: Emitted[]; valid: boolean } {
  const emitted: Emitted[] = []
  let html = ''
  let valid = true
  // Every stamp inside the element must belong to its own subtree in the
  // source, once. A stamp from elsewhere is an element that was moved in.
  const own = idxOf(el)
  const last = doc.nodes[own]?.last ?? own
  const seen = new Set<number>()
  const walk = (parent: Node) => {
    for (const child of parent.childNodes) {
      if (child.nodeType === 3) {
        html += escapeText((child as Text).data)
        continue
      }
      if (child.nodeType !== 1) continue
      const c = child as Element
      const idx = idxOf(c)
      const node = idx >= 0 ? doc.nodes[idx] : undefined
      if (node && node.loc) {
        if (idx <= own || idx > last || seen.has(idx)) valid = false
        seen.add(idx)
        if (!INLINE_TAGS.has(node.tag)) {
          // A locked box is copied from the bytes its stamp names. A box that
          // moved (a drag, a browser that ignored the cancel) still carries its
          // old stamp, which then names a DIFFERENT box: its data attributes
          // give that away, and the whole edit is refused.
          if (node.tag !== c.localName.toLowerCase() || !sameDataAttrs(c, node.attrs)) valid = false
          html += outerSource(doc, idx)
          emitted.push({ el: c, size: node.last - node.idx + 1 })
          continue
        }
        html += startTagSource(doc, idx)
        emitted.push({ el: c, size: 1 })
        if (node.tag === 'br') continue
        walk(c)
        html += endTagSource(doc, idx)
        continue
      }
      const tag = c.localName.toLowerCase()
      if (tag === 'br') {
        html += '<br>'
        emitted.push({ el: c, size: 1 })
      } else if (TYPED_INLINE.has(tag)) {
        html += `<${tag}>`
        emitted.push({ el: c, size: 1 })
        walk(c)
        html += `</${tag}>`
      } else {
        walk(c)
      }
    }
  }
  walk(el)
  return { html, emitted, valid }
}

/** The attributes that say which data a locked box shows. */
export const DATA_ATTRS = ['from', 'row', 'col', 'rows', 'cols', 'type'] as const

function sameDataAttrs(el: Element, attrs: Record<string, string>): boolean {
  return DATA_ATTRS.every((name) => (el.getAttribute(name) ?? undefined) === attrs[name])
}

/** The editing host (a prose element or a field) a node sits in, or null. */
export function editingHostOf(node: Node | null): Element | null {
  const el = !node ? null : node.nodeType === 1 ? (node as Element) : node.parentElement
  return el?.closest(`[${ROLE_ATTR}="prose"], [${ROLE_ATTR}="attr"]`) ?? null
}

/**
 * Whether the editor must cancel a `beforeinput`: a drag-and-drop (which can
 * carry a locked figure from one paragraph to another), or any edit whose
 * range runs across two editing hosts (which would merge two elements the
 * source keeps apart).
 */
export function shouldBlockInput(e: { inputType: string; getTargetRanges?: () => StaticRange[] }): boolean {
  if (e.inputType === 'insertFromDrop' || e.inputType === 'deleteByDrag') return true
  for (const r of e.getTargetRanges?.() ?? []) {
    if (editingHostOf(r.startContainer) !== editingHostOf(r.endContainer)) return true
  }
  return false
}

/** Whitespace-collapsed text of a field that writes an attribute. */
export function fieldText(el: Element): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

export interface ProseBaseline {
  idx: number
  /** The element's content bytes when editing began. */
  inner: string
  /** What `serializeInner` gave when editing began: if it gives this again, the original bytes go back. */
  serialized: string
}

export function proseBaseline(el: Element, doc: StoryDoc): ProseBaseline {
  const idx = idxOf(el)
  const loc = doc.nodes[idx].loc!
  return {
    idx,
    inner: doc.source.slice(loc.startTagEnd, loc.endTagStart ?? loc.startTagEnd),
    serialized: serializeInner(el, doc).html,
  }
}

export interface CommitResult {
  source: string
  doc: StoryDoc
  /** False when the stamps could not be brought in line with the new source: remount before the next edit. */
  stampsValid: boolean
  /**
   * True when the page no longer matches its stamps in a way that cannot be
   * written back (a locked box moved). `source` is then the unchanged source,
   * and the caller remounts to put the page back.
   */
  refused: boolean
}

/**
 * Write a typed edit of prose element `el` back into the source, and re-stamp
 * the page so ordinals keep matching the new source (an edit can add or drop
 * inline elements, which shifts every ordinal after it).
 */
export function commitProse(main: Element, el: Element, doc: StoryDoc, baseline?: ProseBaseline): CommitResult {
  const idx = idxOf(el)
  const node = doc.nodes[idx]
  const { html, emitted, valid } = serializeInner(el, doc)
  if (!valid) return { source: doc.source, doc, stampsValid: false, refused: true }
  const inner = baseline && baseline.idx === idx && html === baseline.serialized ? baseline.inner : html
  const source = editInner(doc.source, idx, inner, doc)
  const next = parseStory(source)

  // Re-stamp: everything after the old subtree moves by the change in size…
  const newLast = idx + emitted.reduce((n, e) => n + e.size, 0)
  const delta = newLast - node.last
  if (delta !== 0) {
    for (const other of main.querySelectorAll(`[${IDX_ATTR}]`)) {
      if (el.contains(other)) continue
      const i = idxOf(other)
      if (i > node.last) other.setAttribute(IDX_ATTR, String(i + delta))
    }
    for (const other of main.querySelectorAll(`[${HOST_ATTR}]`)) {
      const i = Number(other.getAttribute(HOST_ATTR))
      if (i > node.last) other.setAttribute(HOST_ATTR, String(i + delta))
    }
  }
  // …and what is inside it takes the ordinals it now has.
  let counter = idx + 1
  for (const e of emitted) {
    e.el.setAttribute(IDX_ATTR, String(counter))
    counter += e.size
  }
  return { source, doc: next, stampsValid: verifyStamps(main, next), refused: false }
}

/** Write a typed edit of a field (a heading drawn from an attribute) back to its attribute. */
export function commitField(el: Element, doc: StoryDoc): { source: string; doc: StoryDoc } {
  const host = Number(el.getAttribute(HOST_ATTR))
  const field = el.getAttribute(FIELD_ATTR) ?? ''
  const source = setAttr(doc.source, host, field, fieldText(el), doc)
  return { source, doc: parseStory(source) }
}

/**
 * Mark the top-level blocks that changed since the last save (storyDiff's
 * `changedElements`) with `DIRTY_ATTR`, and clear it from every other
 * stamped element. Called after every edit: ordinals shift, so this is never
 * cached against a previous call.
 */
export function markDirty(main: Element, changed: number[]) {
  const dirty = new Set(changed)
  for (const el of main.querySelectorAll(`[${IDX_ATTR}]`)) {
    if (dirty.has(idxOf(el))) el.setAttribute(DIRTY_ATTR, '')
    else el.removeAttribute(DIRTY_ATTR)
  }
}

/** Every stamped element names an element of the same tag in `doc`. */
export function verifyStamps(main: Element, doc: StoryDoc): boolean {
  for (const el of main.querySelectorAll(`[${IDX_ATTR}]`)) {
    const node = doc.nodes[idxOf(el)]
    if (!node || node.tag !== el.localName.toLowerCase()) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Mounting inside the iframe
// ---------------------------------------------------------------------------

type RuntimeWindow = Window & { __ptStoryRuntime?: StoryRuntime }

/**
 * The page's own story runtime, imported inside the iframe so it is the SAME
 * module instance the shell loaded (one provenance handler, one dataset cache).
 */
export function loadRuntime(win: Window, timeoutMs = 15000): Promise<StoryRuntime> {
  const w = win as RuntimeWindow
  if (w.__ptStoryRuntime) return Promise.resolve(w.__ptStoryRuntime)
  return new Promise((resolve, reject) => {
    const doc = win.document
    const done = () => {
      if (w.__ptStoryRuntime) resolve(w.__ptStoryRuntime)
      else reject(new Error('the page did not load its story runtime (lib/story.js)'))
    }
    const timer = win.setTimeout(done, timeoutMs)
    win.addEventListener('pt-story-runtime', () => {
      win.clearTimeout(timer)
      done()
    }, { once: true })
    const script = doc.createElement('script')
    script.type = 'module'
    script.textContent =
      "import * as rt from './lib/story.js';" +
      'window.__ptStoryRuntime = rt;' +
      "window.dispatchEvent(new Event('pt-story-runtime'));"
    doc.head.appendChild(script)
  })
}

function waitFor<T>(win: Window, probe: () => T | null, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    const start = Date.now()
    const tick = () => {
      const v = probe()
      if (v || Date.now() - start > timeoutMs) resolve(v)
      else win.setTimeout(tick, 50)
    }
    tick()
  })
}

export function ensureEditorStyles(doc: Document) {
  if (doc.getElementById(EDITOR_STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = EDITOR_STYLE_ID
  style.textContent = EDITOR_CSS
  doc.head.appendChild(style)
}

/**
 * Mount `doc.source` as the page's story, stamped and marked for editing.
 * Replaces whatever story is mounted (the shell's own, or an earlier editor
 * mount), keeping the brand bar the shell drew above it.
 */
export async function mountStoryForEditing(
  win: Window,
  doc: StoryDoc,
  opts: { runtime?: StoryRuntime; timeoutMs?: number; readOnly?: boolean } = {},
): Promise<MountResult> {
  const d = win.document
  const runtime = opts.runtime ?? (await loadRuntime(win, opts.timeoutMs))
  const existing = await waitFor(win, () => d.querySelector('main.pt-story'), opts.timeoutMs ?? 15000)
  const main = d.createElement('main')
  main.className = 'pt-story'
  main.setAttribute('data-pt-editor', '')
  main.innerHTML = doc.source
  const parity = stampElements(main, doc)
  if (existing) existing.replaceWith(main)
  else (d.getElementById('app') ?? d.body).appendChild(main)
  ensureEditorStyles(d)
  // Hidden sections are drawn (dimmed) in the editor, so they are filled too.
  const errors = await runtime.hydrate(main, { includeHidden: true })
  // Always stamp for editing, locked or not (#1578): a page that mounted
  // while another person held the edit lease used to skip this altogether,
  // so nothing ever became editable when the lease was later released (the
  // draft's source hadn't changed, so nothing remounted to try again).
  // `setContentEditable` is what actually enforces read-only, live, on every
  // render — this only needs to run once, whenever parity holds.
  if (parity) markEditable(main, doc)
  if (opts.readOnly) setContentEditable(main, false)
  return { main, errors, parity, kit: !!runtime.STORY_TAGS?.includes('pt-callout') }
}

/**
 * Toggle every prose/attr element's `contenteditable` without touching the
 * ROLE_ATTR/HOST_ATTR stamps (#1578): the editor goes read-only while another
 * person holds the edit lease, and back once they leave, without a remount.
 */
export function setContentEditable(main: Element, editable: boolean) {
  for (const el of main.querySelectorAll(`[${ROLE_ATTR}="prose"], [${ROLE_ATTR}="attr"]`)) {
    el.setAttribute('contenteditable', editable ? 'true' : 'false')
  }
}

/**
 * What a section control will act on, said above the section: "Section: In
 * short", "(hidden)" when it is hidden.
 */
export function sectionLabel(doc: StoryDoc, idx: number): string {
  const node = doc.nodes[idx]
  const title = (node?.attrs.title ?? '').trim()
  const hidden = isHidden(doc, idx) ? ' (hidden)' : ''
  return `${title ? `Section: ${title}` : 'Section'}${hidden}`
}

/**
 * Outline the whole top-level section `idx` and dim the rest of the story, so
 * a reader sees that a section control moves the heading, the figures and
 * the text together. Replaces any earlier spotlight.
 */
export function spotlightSection(main: Element, idx: number, label: string) {
  clearSpotlight(main)
  const el = main.querySelector(`:scope > [${IDX_ATTR}="${idx}"]`)
  if (!el) return
  el.setAttribute(SPOT_LABEL_ATTR, label)
  main.setAttribute(SPOT_ATTR, String(idx))
}

export function clearSpotlight(main: Element) {
  main.removeAttribute(SPOT_ATTR)
  for (const el of main.querySelectorAll(`[${SPOT_LABEL_ATTR}]`)) el.removeAttribute(SPOT_LABEL_ATTR)
}

/**
 * The element a pointer event is about, for the editor: the locked box under
 * it, the top-level block, and the box whose settings it belongs to (`host`,
 * settingsHostOf), each -1 when there is none.
 */
export function targetsOf(target: EventTarget | null, main: Element, doc: StoryDoc): { leaf: number; block: number; host: number } {
  // Duck-typed, not `instanceof Element`: the target lives in the iframe's
  // realm, whose Element is not this window's.
  const node = target as Node | null
  let el: Element | null = !node ? null : node.nodeType === 1 ? (node as Element) : node.parentElement
  let leaf = -1
  let nearest = -1
  while (el && el !== main && main.contains(el)) {
    const i = idxOf(el)
    if (i >= 0) {
      if (nearest === -1) nearest = i
      if (leaf === -1 && isLeafTag(doc.nodes[i]?.tag ?? '')) leaf = i
    }
    el = el.parentElement
  }
  return {
    leaf,
    block: nearest === -1 ? -1 : blockOf(doc, nearest),
    host: nearest === -1 ? -1 : settingsHostOf(doc, nearest),
  }
}
